import * as EppoSdk from '@eppo/js-client-sdk';
import { createContext, useContext, useMemo } from 'react';

import { useDeviceType } from '~/hooks/useDeviceType';

import { AuthenticationContext } from '../AuthenticationProvider/AuthenticationProvider';

const eppoClient = EppoSdk.getInstance();
const IS_LHCI_TESTING = process.env.LHCI_TESTING;
const EPPO_SUBJECT_KEY_STORAGE_KEY = 'mp_eppo_subject_key';

const DEFAULT_STRING = 'off' as const;
const DEFAULT_JSON: object = {} as const;

type Context = {
  isError: boolean;
  isInitialized: boolean;
  subjectKey: string;
};

export const EppoContext = createContext<Context>({
  isInitialized: false,
  isError: false,
  subjectKey: '',
});

export const useEppo = (): Context => useContext(EppoContext);

type SubjectAttrs = {
  city: string;
  deviceType: string;
  isSitter: boolean | 'unset';
  postCode: string;
  state: string;
  userId: string | number | '';
};

export const getOrCreateFallbackSubjectKey = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const existingKey = window.localStorage.getItem(EPPO_SUBJECT_KEY_STORAGE_KEY);

    if (existingKey) {
      return existingKey;
    }

    const generatedKey =
      window.crypto?.randomUUID?.() ||
      `mp-eppo-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    window.localStorage.setItem(EPPO_SUBJECT_KEY_STORAGE_KEY, generatedKey);

    return generatedKey;
  } catch {
    return '';
  }
};

const useSubject = (): { subjectAttributes: SubjectAttrs; subjectKey: string } => {
  /**
   * Cannot use `useAuthenticationContext` here (circular dep).
   * Use the context directly.
   */
  const { user } = useContext(AuthenticationContext);
  const deviceType = useDeviceType();
  const { subjectKey } = useEppo();

  const subjectAttributes: SubjectAttrs = useMemo(
    () => ({
      city: user?.city || '',
      isSitter: user?.isSitter !== undefined ? user.isSitter : 'unset',
      state: user?.state || '',
      postCode: user?.postcode || '',
      userId: user?.id ?? '',
      deviceType,
    }),
    [user, deviceType]
  );

  return { subjectKey, subjectAttributes };
};

const useEppoAssignment = <T>(
  resolver: (
    client: ReturnType<typeof EppoSdk.getInstance>,
    subjectKey: string,
    subjectAttributes: SubjectAttrs,
    defaultValue: T
  ) => T,
  defaultValue: T
): T => {
  const { isInitialized } = useEppo();
  const { subjectKey, subjectAttributes } = useSubject();

  const assignment = useMemo(() => {
    // Ensure tests and uninitialized client always use default
    if (!isInitialized || IS_LHCI_TESTING) {
      return defaultValue;
    }

    return resolver(eppoClient, subjectKey, subjectAttributes, defaultValue);
  }, [defaultValue, resolver, isInitialized, subjectKey, subjectAttributes]);

  return assignment;
};

export const useEppoStringAssignment = (
  featureKey: string,
  defaultString: string = DEFAULT_STRING
): string =>
  useEppoAssignment<string>(
    (client, subjectKey, subjectAttributes, defaultValue) =>
      client.getStringAssignment(featureKey, subjectKey, subjectAttributes, defaultValue),
    defaultString
  );

export const useEppoJSONAssignment = (featureKey: string): object =>
  useEppoAssignment<object>(
    (client, subjectKey, subjectAttributes, defaultValue) =>
      client.getJSONAssignment(featureKey, subjectKey, subjectAttributes, defaultValue),
    DEFAULT_JSON
  );

export const validateEppoJson = (
  json: Record<string, unknown>,
  expectedKeys: Record<string, string>
): boolean => {
  if (typeof json !== 'object' || json === null) {
    return false;
  }

  for (const [key, type] of Object.entries(expectedKeys)) {
    if (!(key in json)) {
      return false;
    }
    if (typeof json[key] !== type) {
      return false;
    }
  }

  return true;
};
